/**
 * A coupon/promo code issued to the customer's account, from the legacy `get_onlinecode`
 * endpoint (MYP-71 built this screen; MYP-73 wired it to the live call).
 *
 * `get_onlinecode` returns every coupon ever issued, long-expired ones included, with no
 * filtering of its own — MYP-71's ticket said so and the live call confirms it: all 78 coupons on
 * the test account are expired. `status` below is this app's own read of a coupon, not the
 * vendor's flag, and `deriveCouponStatus` is what applies it.
 *
 * **The vendor's own status label is deliberately not used here**, which is the opposite of the
 * call `Package` and `SubmittedOrder` make. Those two carry `statusLabel` because the vendor's
 * word for a package or an order is more precise than this app's. A coupon's is not: every row
 * with `status: "N"` comes back labelled 无效 / "invalid", including rows this app would call
 * valid. Rendering that would tell a customer a usable coupon is worthless, so the label stays out
 * and the status is derived.
 */
export interface Coupon {
  readonly id: string;
  readonly code: string;
  /**
   * What kind of coupon this is, in the reader's language — the vendor's own `typename` /
   * `typename_en` ("Air order discount coupon", 海运折扣券). Unlike the status label above, this
   * one *is* worth carrying through: it is a fact about the coupon, not a judgement about whether
   * it can be used, and the vendor publishes ten of them.
   */
  readonly title: string;
  /** The vendor's code for that type, e.g. `AIRFEEREQ`. Kept for grouping and for debugging. */
  readonly typeCode: string;
  /** Face value, in integer cents — see money.ts. */
  readonly valueCents: number;
  /**
   * The currency as the vendor spells it — `"RM"`, `"RMB"`. **Not an ISO 4217 code**, and this
   * account's list mixes two of them. Everything that formats it goes through `toIsoCurrency`;
   * see the note there for what happens if anything doesn't.
   */
  readonly currency: string;
  /** ISO 8601 — the date this coupon becomes usable. */
  readonly startDate: string;
  /** ISO 8601 — the date this coupon stops being usable. */
  readonly endDate: string;
  /**
   * The smallest order this coupon may be applied to, in integer cents, or undefined when the
   * vendor sets none.
   *
   * Absent from this type until MYP-73 looked at real rows, where `leastcost` runs 30 / 50 / 100 /
   * 150 against face values of 3 to 70. "RM 20 off" shown without "on orders over RM 30" is not a
   * shorter way of saying the same thing — it is a different promise.
   */
  readonly minSpendCents?: number;
  /**
   * Whether the vendor has already marked this coupon spent. Independent of expiry — a coupon
   * can be used well before its `endDate`, and one that was never used still needs its own
   * expiry check (see `deriveCouponStatus`).
   */
  readonly used: boolean;
}

export type CouponStatus = 'valid' | 'scheduled' | 'used' | 'expired';

/**
 * The status a coupon actually shows, combining the vendor's own `used` flag with a date check
 * this app runs itself against `now`.
 *
 * Three rules, in this order:
 *
 * - `used` wins over everything. A coupon spent before it expired is "used", not "expired" —
 *   that's the more informative thing to tell the customer, and it's what the mentor mockup shows.
 * - A coupon whose window has not opened is `scheduled`, not `valid`. The vendor issues coupons
 *   with a future `strdate`, and calling one valid invites a customer to try spending it today.
 * - Otherwise it is `valid` until its `endDate` passes.
 */
export function deriveCouponStatus(coupon: Coupon, now: Date): CouponStatus {
  if (coupon.used) return 'used';
  if (new Date(coupon.startDate) > now) return 'scheduled';
  return new Date(coupon.endDate) < now ? 'expired' : 'valid';
}

/**
 * Usable first, then not-yet-usable, then used, then expired — the mentor mockup's ordering, with
 * `scheduled` slotted where it belongs: after what can be spent now, before what never can be.
 *
 * Ties within a group break by expiry date, most recent lapse on top, so a coupon that just
 * expired doesn't get buried under one that lapsed a year ago.
 */
export function sortCoupons(coupons: readonly Coupon[], now: Date): readonly Coupon[] {
  const rank: Record<CouponStatus, number> = { valid: 0, scheduled: 1, used: 2, expired: 3 };
  return [...coupons].sort((a, b) => {
    const statusDiff = rank[deriveCouponStatus(a, now)] - rank[deriveCouponStatus(b, now)];
    if (statusDiff !== 0) return statusDiff;
    return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
  });
}

/** How many coupons the customer can actually spend right now — for a badge or a summary line. */
export function countUsableCoupons(coupons: readonly Coupon[], now: Date): number {
  return coupons.filter((coupon) => deriveCouponStatus(coupon, now) === 'valid').length;
}
